/**
 * ActeursLocauxManager — gestion des acteurs locaux (mairie / Associations).
 *
 * Annuaire économique (commerces, artisans, producteurs…). Pour les producteurs,
 * gestion des produits/paniers en circuit court. Greffé sur la page Associations.
 *
 * Conforme au guide : zéro window.confirm (useConfirmDialog + {dialogElement}).
 */

import { useEffect, useState, useCallback } from 'react';
import { Button, Badge, Alert, Field, Input } from '@/components/ui';
import { Dialog } from '@/components/ui/Dialog';
import { useConfirmDialog } from '@/lib/use-confirm-dialog';
import { api, ApiException } from '@/lib/api';
import type { ActeurLocal, ActeurProduit, ActeurCategorie, ProduitType } from '@/lib/api';
import {
  Store, Plus, Pencil, Trash2, MapPin, Phone, Mail, Globe, Clock, Sprout,
  Package, EyeOff, AlertCircle,
} from 'lucide-react';

const CATEGORIE_LABEL: Record<ActeurCategorie, string> = {
  producteur: 'Producteur', commerce: 'Commerce', artisan: 'Artisan', service: 'Service',
  restauration: 'Restauration', sante: 'Santé', profession_liberale: 'Profession libérale', autre: 'Autre',
};
const CATEGORIE_VARIANT: Record<ActeurCategorie, 'success' | 'info' | 'warning' | 'neutral' | 'grc'> = {
  producteur: 'success', commerce: 'info', artisan: 'warning', service: 'neutral',
  restauration: 'warning', sante: 'info', profession_liberale: 'neutral', autre: 'neutral',
};
const PRODUIT_TYPE_LABEL: Record<ProduitType, string> = { panier: 'Panier', produit: 'Produit', abonnement: 'Abonnement' };

function euros(v: number | null): string {
  if (v == null) return '';
  return `${v.toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`;
}

export function ActeursLocauxManager({ onEtatChange }: { onEtatChange?: () => void }) {
  const { confirm, dialogElement } = useConfirmDialog();
  const [acteurs, setActeurs] = useState<ActeurLocal[]>([]);
  const [enAttente, setEnAttente] = useState<ActeurLocal[]>([]);
  const [filtre, setFiltre] = useState<string>('');
  const [q, setQ] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [edit, setEdit] = useState<ActeurLocal | 'new' | null>(null);
  const [produitsDe, setProduitsDe] = useState<ActeurLocal | null>(null);

  const reload = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const [tous, attente] = await Promise.all([
        api.associations.acteurs.list({ categorie: filtre || undefined, q: q.trim() || undefined }),
        api.associations.acteurs.list({ statutValidation: 'en_attente' }),
      ]);
      // Liste principale : on masque les fiches en attente (traitées à part).
      setActeurs(tous.acteurs.filter((a) => a.statutValidation !== 'en_attente'));
      setEnAttente(attente.acteurs);
    }
    catch (e) { setError(e instanceof ApiException ? e.message : 'Chargement impossible'); }
    finally { setLoading(false); }
  }, [filtre, q]);
  useEffect(() => { void reload(); }, [reload]);
  async function refreshAll() { await reload(); onEtatChange?.(); }

  async function supprimer(a: ActeurLocal) {
    const ok = await confirm({ title: 'Supprimer cet acteur ?', description: `${a.nom}. Ses produits seront aussi supprimés.`, confirmLabel: 'Supprimer', danger: true });
    if (!ok) return;
    try { await api.associations.acteurs.remove(a.id); await refreshAll(); }
    catch (e) { setError(e instanceof ApiException ? e.message : 'Suppression impossible'); }
  }

  async function valider(a: ActeurLocal) {
    try { await api.associations.acteurs.valider(a.id); await refreshAll(); }
    catch (e) { setError(e instanceof ApiException ? e.message : 'Validation impossible'); }
  }

  async function refuser(a: ActeurLocal) {
    const ok = await confirm({ title: 'Refuser cette déclaration ?', description: `La fiche « ${a.nom} » ne sera pas publiée.`, confirmLabel: 'Refuser', danger: true });
    if (!ok) return;
    try { await api.associations.acteurs.refuser(a.id); await refreshAll(); }
    catch (e) { setError(e instanceof ApiException ? e.message : 'Refus impossible'); }
  }

  const selectCls = 'rounded-r-md border border-border bg-bg px-3 py-1.5 text-sm text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-grc';

  return (
    <div>
      {error && <Alert variant="error" icon={AlertCircle} className="mb-3">{error}</Alert>}

      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <div className="flex flex-wrap items-center gap-2">
          <select className={selectCls} value={filtre} onChange={(e) => setFiltre(e.target.value)}>
            <option value="">Toutes catégories</option>
            {Object.entries(CATEGORIE_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </select>
          <input className={`${selectCls} min-w-[180px]`} placeholder="Rechercher…" value={q}
            onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') void reload(); }} />
        </div>
        <Button variant="primary" size="sm" onClick={() => setEdit('new')}><Plus className="h-4 w-4" /> Acteur</Button>
      </div>

      {/* Demandes d'auto-déclaration en attente de validation (migration 133) */}
      {enAttente.length > 0 && (
        <div className="mb-4 rounded-r-lg border border-warning/40 bg-warning-tint/30 p-3">
          <div className="mb-2 inline-flex items-center gap-1.5 text-sm font-medium text-warning">
            <Clock className="h-4 w-4" /> {enAttente.length} demande{enAttente.length > 1 ? 's' : ''} de référencement à valider
          </div>
          <ul className="space-y-2">
            {enAttente.map((a) => (
              <li key={a.id} className="rounded-r-md border border-border bg-surface/60 p-3">
                <div className="flex flex-wrap items-start justify-between gap-2">
                  <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-2 font-medium text-ink">
                      <span>{a.nom}</span>
                      <Badge variant={CATEGORIE_VARIANT[a.categorie]} size="sm">{CATEGORIE_LABEL[a.categorie]}</Badge>
                    </div>
                    {a.activite && <div className="mt-0.5 text-sm text-ink-muted">{a.activite}</div>}
                    {a.description && <p className="mt-0.5 text-sm text-ink-muted">{a.description}</p>}
                    <div className="mt-0.5 flex flex-wrap gap-x-3 text-xs text-ink-dim">
                      {a.telephone && <span className="inline-flex items-center gap-1"><Phone className="h-3 w-3" />{a.telephone}</span>}
                      {a.email && <span className="inline-flex items-center gap-1"><Mail className="h-3 w-3" />{a.email}</span>}
                      {(a.adresse || a.ville) && <span className="inline-flex items-center gap-1"><MapPin className="h-3 w-3" />{[a.adresse, a.ville].filter(Boolean).join(', ')}</span>}
                    </div>
                  </div>
                  <div className="flex flex-shrink-0 gap-2">
                    <Button variant="primary" size="sm" onClick={() => void valider(a)}>Valider</Button>
                    <Button variant="ghost" size="sm" onClick={() => void refuser(a)} className="text-ink-muted hover:text-urgent">Refuser</Button>
                  </div>
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}

      {loading ? (
        <p className="text-sm text-ink-muted">Chargement…</p>
      ) : acteurs.length === 0 ? (
        <p className="text-sm text-ink-muted">Aucun acteur. Ajoutez commerces, artisans, producteurs de la commune.</p>
      ) : (
        <ul className="space-y-2">
          {acteurs.map((a) => (
            <li key={a.id} className={`rounded-r-lg border border-border bg-surface/50 p-3 ${!a.publie || !a.actif ? 'opacity-60' : ''}`}>
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-2 font-medium text-ink">
                    <span>{a.nom}</span>
                    <Badge variant={CATEGORIE_VARIANT[a.categorie]} size="sm">{CATEGORIE_LABEL[a.categorie]}</Badge>
                    {!a.publie && <Badge variant="neutral" size="sm"><EyeOff className="mr-1 inline h-3 w-3" />Non publié</Badge>}
                    {a.categorie === 'producteur' && (a.produits?.length ?? 0) > 0 && (
                      <Badge variant="success" size="sm"><Package className="mr-1 inline h-3 w-3" />{a.produits!.length} produit{a.produits!.length > 1 ? 's' : ''}</Badge>
                    )}
                  </div>
                  {a.activite && <div className="mt-0.5 text-sm text-ink-muted">{a.activite}</div>}
                  <div className="mt-0.5 flex flex-wrap gap-x-3 text-xs text-ink-dim">
                    {a.telephone && <span className="inline-flex items-center gap-1"><Phone className="h-3 w-3" />{a.telephone}</span>}
                    {a.email && <span className="inline-flex items-center gap-1"><Mail className="h-3 w-3" />{a.email}</span>}
                    {(a.adresse || a.ville) && <span className="inline-flex items-center gap-1"><MapPin className="h-3 w-3" />{[a.adresse, a.ville].filter(Boolean).join(', ')}</span>}
                  </div>
                </div>
                <div className="flex flex-shrink-0 gap-1">
                  {a.categorie === 'producteur' && (
                    <Button variant="ghost" size="sm" onClick={() => setProduitsDe(a)} title="Gérer les produits"><Sprout className="h-4 w-4" /></Button>
                  )}
                  <Button variant="ghost" size="sm" onClick={() => setEdit(a)} title="Modifier"><Pencil className="h-4 w-4" /></Button>
                  <Button variant="ghost" size="sm" onClick={() => void supprimer(a)} title="Supprimer"><Trash2 className="h-4 w-4" /></Button>
                </div>
              </div>
            </li>
          ))}
        </ul>
      )}

      {edit && (
        <ActeurDialog initial={edit === 'new' ? null : edit}
          onClose={() => setEdit(null)} onSaved={async () => { setEdit(null); await refreshAll(); }} />
      )}
      {produitsDe && (
        <ProduitsDialog acteur={produitsDe} onClose={() => setProduitsDe(null)} onChanged={refreshAll} confirm={confirm} />
      )}
      {dialogElement}
    </div>
  );
}

// ============================================================================
function ActeurDialog({ initial, onClose, onSaved }: { initial: ActeurLocal | null; onClose: () => void; onSaved: () => void }) {
  const [nom, setNom] = useState(initial?.nom ?? '');
  const [categorie, setCategorie] = useState<ActeurCategorie>(initial?.categorie ?? 'commerce');
  const [activite, setActivite] = useState(initial?.activite ?? '');
  const [description, setDescription] = useState(initial?.description ?? '');
  const [telephone, setTelephone] = useState(initial?.telephone ?? '');
  const [email, setEmail] = useState(initial?.email ?? '');
  const [adresse, setAdresse] = useState(initial?.adresse ?? '');
  const [ville, setVille] = useState(initial?.ville ?? '');
  const [siteWeb, setSiteWeb] = useState(initial?.siteWeb ?? '');
  const [horaires, setHoraires] = useState(initial?.horaires ?? '');
  const [publie, setPublie] = useState(initial?.publie ?? true);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const selectCls = 'w-full rounded-r-md border border-border bg-bg px-3 py-2 text-sm text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-grc';

  async function save() {
    setBusy(true); setErr(null);
    try {
      const body: Record<string, unknown> = {
        nom: nom.trim(), categorie, activite: activite.trim() || null, description: description.trim() || null,
        telephone: telephone.trim() || null, email: email.trim() || null, adresse: adresse.trim() || null,
        ville: ville.trim() || null, siteWeb: siteWeb.trim() || null, horaires: horaires.trim() || null, publie,
      };
      if (initial) await api.associations.acteurs.update(initial.id, body);
      else await api.associations.acteurs.create(body);
      onSaved();
    } catch (e) { setErr(e instanceof ApiException ? e.message : 'Enregistrement impossible'); setBusy(false); }
  }

  return (
    <Dialog open onClose={onClose} title={initial ? 'Modifier l\'acteur' : 'Nouvel acteur local'}>
      <div className="max-h-[70vh] space-y-3 overflow-y-auto pr-1">
        {err && <Alert variant="error" icon={AlertCircle}>{err}</Alert>}
        <div className="grid grid-cols-2 gap-3">
          <Field label="Nom" htmlFor="ac-nom" required><Input id="ac-nom" value={nom} onChange={(e) => setNom(e.target.value)} placeholder="Ferme des Trois Chênes" /></Field>
          <Field label="Catégorie" htmlFor="ac-cat" required>
            <select id="ac-cat" className={selectCls} value={categorie} onChange={(e) => setCategorie(e.target.value as ActeurCategorie)}>
              {Object.entries(CATEGORIE_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
            </select>
          </Field>
        </div>
        <Field label="Activité" htmlFor="ac-act"><Input id="ac-act" value={activite} onChange={(e) => setActivite(e.target.value)} placeholder="Maraîchage bio, boulangerie…" /></Field>
        <Field label="Description" htmlFor="ac-desc">
          <textarea id="ac-desc" rows={2} className="w-full rounded-r-md border border-border bg-bg px-3 py-2 text-sm text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-grc"
            value={description} onChange={(e) => setDescription(e.target.value)} />
        </Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Téléphone" htmlFor="ac-tel"><Input id="ac-tel" value={telephone} onChange={(e) => setTelephone(e.target.value)} /></Field>
          <Field label="Email" htmlFor="ac-email"><Input id="ac-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} /></Field>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Adresse" htmlFor="ac-adr"><Input id="ac-adr" value={adresse} onChange={(e) => setAdresse(e.target.value)} /></Field>
          <Field label="Ville" htmlFor="ac-ville"><Input id="ac-ville" value={ville} onChange={(e) => setVille(e.target.value)} /></Field>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Site web" htmlFor="ac-web"><Input id="ac-web" value={siteWeb} onChange={(e) => setSiteWeb(e.target.value)} placeholder="https://…" /></Field>
          <Field label="Horaires" htmlFor="ac-hor"><Input id="ac-hor" value={horaires} onChange={(e) => setHoraires(e.target.value)} placeholder="Mar-Sam 8h-13h" /></Field>
        </div>
        <label className="flex items-center gap-2 text-sm text-ink">
          <input type="checkbox" checked={publie} onChange={(e) => setPublie(e.target.checked)} className="h-4 w-4 rounded border-border text-grc focus-visible:ring-grc" />
          Publié dans l'annuaire des habitants
        </label>
        <div className="flex justify-end gap-2 pt-2">
          <Button variant="ghost" onClick={onClose} disabled={busy}>Annuler</Button>
          <Button variant="primary" onClick={save} disabled={busy || nom.trim().length < 2}>{busy ? 'Enregistrement…' : 'Enregistrer'}</Button>
        </div>
      </div>
    </Dialog>
  );
}

// ============================================================================
function ProduitsDialog({ acteur, onClose, onChanged, confirm }: {
  acteur: ActeurLocal; onClose: () => void; onChanged: () => void;
  confirm: (o: { title: string; description?: string; confirmLabel?: string; danger?: boolean }) => Promise<boolean>;
}) {
  const [produits, setProduits] = useState<ActeurProduit[]>(acteur.produits ?? []);
  const [ajout, setAjout] = useState(false);
  const [nom, setNom] = useState(''); const [type, setType] = useState<ProduitType>('panier');
  const [prix, setPrix] = useState(''); const [unite, setUnite] = useState(''); const [saison, setSaison] = useState('');
  const [busy, setBusy] = useState(false); const [err, setErr] = useState<string | null>(null);

  const load = useCallback(async () => {
    try { setProduits((await api.associations.acteurs.get(acteur.id)).produits ?? []); }
    catch (e) { setErr(e instanceof ApiException ? e.message : 'Chargement impossible'); }
  }, [acteur.id]);

  async function ajouter() {
    setBusy(true); setErr(null);
    try {
      await api.associations.acteurs.addProduit(acteur.id, {
        nom: nom.trim(), type, prixEuros: prix.trim() ? Number(prix) : null, unite: unite.trim() || null, saison: saison.trim() || null,
      });
      setNom(''); setPrix(''); setUnite(''); setSaison(''); setAjout(false);
      await load(); onChanged();
    } catch (e) { setErr(e instanceof ApiException ? e.message : 'Ajout impossible'); }
    finally { setBusy(false); }
  }
  async function supprimer(p: ActeurProduit) {
    const ok = await confirm({ title: 'Supprimer ce produit ?', description: p.nom, confirmLabel: 'Supprimer', danger: true });
    if (!ok) return;
    try { await api.associations.acteurs.removeProduit(p.id); await load(); onChanged(); }
    catch (e) { setErr(e instanceof ApiException ? e.message : 'Suppression impossible'); }
  }

  const selectCls = 'w-full rounded-r-md border border-border bg-bg px-3 py-2 text-sm text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-grc';

  return (
    <Dialog open onClose={onClose} title={`Produits — ${acteur.nom}`}>
      <div className="max-h-[70vh] space-y-3 overflow-y-auto pr-1">
        {err && <Alert variant="error" icon={AlertCircle}>{err}</Alert>}
        <div className="flex items-center justify-between">
          <div className="inline-flex items-center gap-1 text-sm font-medium text-ink"><Package className="h-4 w-4 text-grc" /> Paniers & produits ({produits.length})</div>
          {!ajout && <Button variant="ghost" size="sm" onClick={() => setAjout(true)}><Plus className="h-4 w-4" /> Ajouter</Button>}
        </div>
        {ajout && (
          <div className="space-y-2 rounded-r-lg border border-border bg-bg-2 p-3">
            <Field label="Nom" htmlFor="p-nom" required><Input id="p-nom" value={nom} onChange={(e) => setNom(e.target.value)} placeholder="Panier de légumes de saison" /></Field>
            <div className="grid grid-cols-3 gap-2">
              <Field label="Type" htmlFor="p-type">
                <select id="p-type" className={selectCls} value={type} onChange={(e) => setType(e.target.value as ProduitType)}>
                  {Object.entries(PRODUIT_TYPE_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                </select>
              </Field>
              <Field label="Prix (€)" htmlFor="p-prix"><Input id="p-prix" type="number" min={0} step="0.01" value={prix} onChange={(e) => setPrix(e.target.value)} /></Field>
              <Field label="Unité" htmlFor="p-unite"><Input id="p-unite" value={unite} onChange={(e) => setUnite(e.target.value)} placeholder="le panier, le kg…" /></Field>
            </div>
            <Field label="Saison / disponibilité" htmlFor="p-saison"><Input id="p-saison" value={saison} onChange={(e) => setSaison(e.target.value)} placeholder="Juin à septembre" /></Field>
            <div className="flex justify-end gap-2">
              <Button variant="ghost" size="sm" onClick={() => setAjout(false)} disabled={busy}>Annuler</Button>
              <Button variant="primary" size="sm" onClick={ajouter} disabled={busy || nom.trim().length < 2}>Ajouter</Button>
            </div>
          </div>
        )}
        {produits.length > 0 && (
          <ul className="space-y-1.5">
            {produits.map((p) => (
              <li key={p.id} className="flex items-center justify-between gap-2 rounded-r-md border border-border bg-surface/50 px-3 py-2 text-sm">
                <div className="min-w-0">
                  <span className="text-ink">{p.nom}</span>
                  <span className="ml-2 text-xs text-ink-dim">
                    {PRODUIT_TYPE_LABEL[p.type]}{p.prixEuros != null ? ` · ${euros(p.prixEuros)}${p.unite ? ` ${p.unite}` : ''}` : ''}{p.saison ? ` · ${p.saison}` : ''}
                  </span>
                </div>
                <button type="button" onClick={() => void supprimer(p)} className="text-ink-dim hover:text-urgent" title="Supprimer"><Trash2 className="h-4 w-4" /></button>
              </li>
            ))}
          </ul>
        )}
        <div className="flex justify-end pt-1"><Button variant="ghost" onClick={onClose}>Fermer</Button></div>
      </div>
    </Dialog>
  );
}
